/**
 * Decode Gmail API message payloads into sender, thread text, and file attachments.
 */

import { htmlToReadableText } from "../../email/html-to-readable-text.js";

export const DRAWINGS_INBOUND_MAILBOX = "drawings@smartgridsolutions.com";

export interface GmailHeader {
  name?: string;
  value?: string;
}

export interface GmailMessagePart {
  mimeType?: string;
  filename?: string;
  headers?: GmailHeader[];
  body?: { data?: string; attachmentId?: string; size?: number };
  parts?: GmailMessagePart[];
}

export interface GmailMessage {
  id?: string;
  threadId?: string;
  internalDate?: string;
  payload?: GmailMessagePart;
}

export interface ParsedInboundAttachment {
  filename: string;
  mimeType: string;
  attachmentId: string | null;
  inlineData: Buffer | null;
  size: number;
}

export interface ParsedInboundMessage {
  messageId: string;
  threadId: string | null;
  fromEmail: string;
  subject: string | null;
  receivedAt: Date | null;
  textBodies: string[];
  attachments: ParsedInboundAttachment[];
}

const INLINE_IMAGE_MAX_BYTES = 20 * 1024;
const SKIP_FILENAME_RE = /\.(ics|vcf)$/i;
const SKIP_NAME_RE = /^(signature|logo|image00\d*)(\.|$)/i;

export function shouldSkipInboundAttachment(filename: string, mimeType: string, size: number, inline: boolean): boolean {
  if (SKIP_FILENAME_RE.test(filename)) return true;
  const mime = mimeType.trim().toLowerCase();
  if (!mime.startsWith("image/")) return false;
  if (filename === "" && size <= INLINE_IMAGE_MAX_BYTES) return true;
  if (inline && size <= INLINE_IMAGE_MAX_BYTES) return true;
  if (SKIP_NAME_RE.test(filename) && size <= INLINE_IMAGE_MAX_BYTES) return true;
  return false;
}

export function decodeGmailBase64Url(data: string): Buffer {
  const padded = data.replace(/-/g, "+").replace(/_/g, "/");
  return Buffer.from(padded, "base64");
}

export function headerValue(headers: GmailHeader[] | undefined, name: string): string | null {
  const want = name.trim().toLowerCase();
  for (const header of headers ?? []) {
    if ((header.name ?? "").trim().toLowerCase() === want) {
      const value = header.value?.trim() ?? "";
      return value === "" ? null : value;
    }
  }
  return null;
}

export function parseEmailAddress(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim() ?? "";
  if (trimmed === "") return null;
  const angle = trimmed.match(/<([^>]+)>/);
  const candidate = (angle?.[1] ?? trimmed).trim().toLowerCase();
  if (!candidate.includes("@")) return null;
  return candidate;
}

function walkParts(part: GmailMessagePart | undefined, into: GmailMessagePart[]): void {
  if (part == null) return;
  into.push(part);
  for (const child of part.parts ?? []) {
    walkParts(child, into);
  }
}

export function parseGmailMessage(message: GmailMessage): ParsedInboundMessage | null {
  const messageId = message.id?.trim() ?? "";
  if (messageId === "") return null;
  const fromEmail = parseEmailAddress(headerValue(message.payload?.headers, "from"));
  if (fromEmail == null) return null;
  const subject = headerValue(message.payload?.headers, "subject");
  const internalMs = message.internalDate != null ? Number.parseInt(message.internalDate, 10) : Number.NaN;
  const receivedAt = Number.isFinite(internalMs) ? new Date(internalMs) : null;

  const parts: GmailMessagePart[] = [];
  walkParts(message.payload, parts);

  const textBodies: string[] = [];
  const attachments: ParsedInboundAttachment[] = [];
  for (const part of parts) {
    const mime = (part.mimeType ?? "application/octet-stream").trim().toLowerCase();
    const filename = (part.filename ?? "").trim();
    const size = part.body?.size ?? (part.body?.data != null ? decodeGmailBase64Url(part.body.data).length : 0);
    if (mime === "text/plain" && part.body?.data && filename === "") {
      textBodies.push(decodeGmailBase64Url(part.body.data).toString("utf8"));
      continue;
    }
    if (mime === "text/html" && part.body?.data && filename === "") {
      textBodies.push(htmlToReadableText(decodeGmailBase64Url(part.body.data).toString("utf8")));
      continue;
    }
    if (filename === "") continue;
    const disposition = (headerValue(part.headers, "content-disposition") ?? "").toLowerCase();
    if (shouldSkipInboundAttachment(filename, mime, size, disposition.includes("inline"))) continue;
    const inlineData = part.body?.data ? decodeGmailBase64Url(part.body.data) : null;
    attachments.push({
      filename,
      mimeType: mime || "application/octet-stream",
      attachmentId: part.body?.attachmentId?.trim() || null,
      inlineData,
      size,
    });
  }

  return {
    messageId,
    threadId: message.threadId?.trim() || null,
    fromEmail,
    subject,
    receivedAt,
    textBodies,
    attachments,
  };
}

export function formatInboundEmailThread(messages: ParsedInboundMessage[]): string {
  const blocks: string[] = [];
  for (const message of messages) {
    const body = message.textBodies.join("\n\n").trim();
    blocks.push(
      [
        `From: ${message.fromEmail}`,
        `Subject: ${message.subject ?? "(no subject)"}`,
        `Date: ${message.receivedAt?.toISOString() ?? "unknown"}`,
        "",
        body === "" ? "(no text body)" : body,
      ].join("\n"),
    );
  }
  return blocks.join("\n\n-----\n\n");
}

/** Human-friendly display for stored inbound email thread text (locale dates). */
export function formatInboundEmailThreadForDisplay(threadText: string): string {
  return threadText.replace(/^Date: (.+)$/gm, (line, rawDate) => {
    const trimmed = rawDate.trim();
    if (trimmed === "unknown") return line;
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return line;
    return `Date: ${parsed.toLocaleString()}`;
  });
}

export function isDrawingsMailboxAddress(email: string): boolean {
  return parseEmailAddress(email) === DRAWINGS_INBOUND_MAILBOX;
}
